/**
 * Timezone arithmetic with no dependencies, built on `Intl`.
 *
 * The launch tenant is `America/St_Thomas`, which has no DST — and that is
 * exactly why this module is careful. A DST-free zone hides every bug in this
 * area until the second tenant is somewhere that isn't, so the conversions here
 * do the real work and the tests exercise a DST zone deliberately.
 */
const formatterCache = new Map();
function formatter(timeZone) {
    let f = formatterCache.get(timeZone);
    if (f === undefined) {
        f = new Intl.DateTimeFormat('en-US', {
            timeZone,
            // h23 rather than `hour12: false`: some ICU builds render midnight as
            // "24" under hour12:false, which would put every midnight on the day before.
            hourCycle: 'h23',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        });
        formatterCache.set(timeZone, f);
    }
    return f;
}
/** The wall-clock reading in `timeZone` at an instant. */
export function toZonedParts(timeZone, utcMs) {
    const parts = formatter(timeZone).formatToParts(new Date(utcMs));
    const get = (type) => {
        const found = parts.find((p) => p.type === type);
        return found === undefined ? 0 : Number(found.value);
    };
    return {
        year: get('year'),
        month: get('month'),
        day: get('day'),
        hour: get('hour'),
        minute: get('minute'),
        second: get('second'),
    };
}
/** The zone's UTC offset, in milliseconds, at an instant. */
export function offsetMs(timeZone, utcMs) {
    const p = toZonedParts(timeZone, utcMs);
    const asIfUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
    // Intl truncates to whole seconds; keep the sub-second part out of the offset.
    return asIfUtc - (utcMs - (utcMs % 1000));
}
/**
 * A wall-clock time in `timeZone` to an instant.
 *
 * Two passes. The first guesses with the offset that applies at the naive
 * instant; the second re-reads the offset at that candidate, which is what
 * corrects a time sitting on the far side of a DST transition. A third pass
 * would never change the answer for any real zone.
 */
export function zonedToUtc(timeZone, year, month, day, hour = 0, minute = 0) {
    const naive = Date.UTC(year, month - 1, day, hour, minute);
    let ts = naive - offsetMs(timeZone, naive);
    ts = naive - offsetMs(timeZone, ts);
    return ts;
}
/** `YYYY-MM-DD` plus `HH:MM`, both read in the tenant's zone, to an instant. */
export function dayTimeToUtc(timeZone, isoDay, hhmm) {
    const [y, m, d] = isoDay.split('-').map(Number);
    const [hh, mi] = hhmm.split(':').map(Number);
    return zonedToUtc(timeZone, y, m, d, hh, mi);
}
/** The calendar date in `timeZone` at an instant, as `YYYY-MM-DD`. */
export function toIsoDay(timeZone, utcMs) {
    const p = toZonedParts(timeZone, utcMs);
    const pad = (n) => String(n).padStart(2, '0');
    return `${p.year}-${pad(p.month)}-${pad(p.day)}`;
}
/** Monday-first weekday index (0 = Monday), matching LoveSpa's `WeekSchedule`. */
export function weekdayIndex(isoDay) {
    const [y, m, d] = isoDay.split('-').map(Number);
    return (new Date(Date.UTC(y, m - 1, d)).getUTCDay() + 6) % 7;
}
/** The inclusive list of calendar dates from `from` to `to`. */
export function eachDay(from, to, maxDays = 400) {
    const [fy, fm, fd] = from.split('-').map(Number);
    const [ty, tm, td] = to.split('-').map(Number);
    const start = Date.UTC(fy, fm - 1, fd);
    const end = Date.UTC(ty, tm - 1, td);
    if (end < start)
        return [];
    const out = [];
    const pad = (n) => String(n).padStart(2, '0');
    for (let t = start; t <= end && out.length < maxDays; t += 86_400_000) {
        const d = new Date(t);
        out.push(`${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`);
    }
    return out;
}
/** `2026-09-03` shifted by whole days, staying a calendar date. */
export function addDays(isoDay, days) {
    const [y, m, d] = isoDay.split('-').map(Number);
    const t = Date.UTC(y, m - 1, d) + days * 86_400_000;
    const dt = new Date(t);
    const pad = (n) => String(n).padStart(2, '0');
    return `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}`;
}
/** Whole-day distance between two calendar dates. */
export function dayDistance(a, b) {
    const [ay, am, ad] = a.split('-').map(Number);
    const [by, bm, bd] = b.split('-').map(Number);
    return Math.round((Date.UTC(by, bm - 1, bd) - Date.UTC(ay, am - 1, ad)) / 86_400_000);
}
/** An instant as ISO-8601 in UTC with an explicit offset — what goes on the wire. */
export function toIsoUtc(utcMs) {
    return new Date(utcMs).toISOString();
}
//# sourceMappingURL=timezone.js.map